/**
 * InputsBuyerInfoProvider keeps state of buyer information
 */
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useInputsState } from 'screens/InputsMarket/providers/InputsStateProvider';
import { useWhiteLabelling } from 'components/WhiteLabellingProvider';
import useIsApprovedUser from 'hooks/useIsApprovedUser';
import { isHeadless } from 'utils/headless';
import { InputsBuyerInfo, useInputsBuyerInfoQuery } from 'generated/graphql';

interface InputsBuyerInfoContextProps {
  buyerInfo: InputsBuyerInfo | undefined;
  isFetching: boolean;
  error: any;
  onRefresh: () => void;
}

export const Context = createContext<InputsBuyerInfoContextProps | undefined>(undefined);

export const InputsBuyerInfoProvider = ({ children }: { children: ReactNode }) => {
  const { isInputsType } = useWhiteLabelling();
  const { queryInput } = useInputsState();
  const isApprovedUser = useIsApprovedUser();

  const [result, onRefresh] = useInputsBuyerInfoQuery({
    variables: {
      id: queryInput.buyerID || '',
    },
    pause: !isInputsType || !queryInput.buyerID || isHeadless() || !isApprovedUser,
  });

  const value = useMemo(
    () => ({
      buyerInfo: result?.data?.inputsBuyerInfo,
      isFetching: result.fetching,
      error: result.error,
      onRefresh: onRefresh,
    }),
    [result?.data?.inputsBuyerInfo, result.fetching, result.error, onRefresh],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputsBuyerInfo = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useInputsBuyerInfo must be used within a InputsBuyerInfoProvider');
  return result;
};
